import { useMemo, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'

import { Icon } from '@wartek-id/icon-v2'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { Checkbox } from '@wartek-id/checkbox-v1'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast-v2'

import { Header } from 'app/KinerjaShared/components/Header'
import {
  useAPIDeleteKinerja,
  useAPIKinerja,
  useAPIUpdateState,
} from 'api/kinerja/useAPIKinerja'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { Detail } from 'app/KinerjaPamongPage/components/perencanaan/Detail'
import { resolvePerencanaanDetailData } from 'app/KinerjaShared/utils/perencanaan'
import { assetUrl } from 'utils/url'
import { useGetKinerjaQuerykeys } from 'api/kinerja/utils/api'
import { apiPaths } from 'api/kinerja/useAPIKinerja'

import type { KinerjaStatus } from 'types/kinerja'
import type { PerencanaanDetailData } from 'types/kinerja'

const ALLOWED_STATES: Array<KinerjaStatus> = ['APPROVED']

export function SepakatiPerencanaan() {
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const perencanaanQueryKey = useGetKinerjaQuerykeys(apiPaths.kinerja())

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const {
    data: perencanaanData,
    isLoading,
    isSuccess,
  } = useAPIKinerja(
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: true,
    },
    {
      tipe: 'SPNF',
    }
  )

  const perencanaanId = perencanaanData?.data?.id

  const resolvedPerencanaanData: PerencanaanDetailData = useMemo(
    () => resolvePerencanaanDetailData(perencanaanData?.data),
    [perencanaanData?.data]
  )

  const allowedToVisit = useMemo(() => {
    return ALLOWED_STATES.includes(perencanaanData?.data?.status)
  }, [perencanaanData?.data?.status])

  // -----------------------------------------
  // Perencanaan state guard
  // -----------------------------------------
  useEffect(() => {
    if (isSuccess && !allowedToVisit) {
      router.replace(PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allowedToVisit, isSuccess])

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------
  const [activeAction, setActiveAction] = useState<
    'SEPAKATI' | 'ULANGI' | 'IDLE'
  >('IDLE')

  const handleOpenSepakatiDialog = () => {
    setActiveAction('SEPAKATI')
  }
  const handleOpenUlangiDialog = () => {
    setActiveAction('ULANGI')
  }
  const handleCloseDialog = () => {
    setActiveAction('IDLE')
  }

  const sepakatiMutation = useAPIUpdateState()
  const ulangiMutation = useAPIDeleteKinerja()

  const handleConfirmSepakati = () => {
    sepakatiMutation.mutate(
      {
        skpId: perencanaanId,
        tipe: 'SPNF',
      },
      {
        onError() {
          toast({
            message: 'Gagal sepakati Perencanaan. Coba lagi.',
          })
        },
        onSuccess() {
          toast({
            type: 'success',
            message: 'Berhasil sepakati Perencanaan.',
          })

          queryClient.invalidateQueries(perencanaanQueryKey)

          router.push(PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH)
        },
      }
    )
  }

  const handleCancelSepakati = () => {
    handleOpenUlangiDialog()
  }

  const handleConfirmUlangi = () => {
    ulangiMutation.mutate(
      {
        tipe: 'SPNF',
      },
      {
        onError() {
          toast({
            message: 'Gagal hapus Perencanaan. Coba lagi.',
          })
        },
        onSuccess() {
          toast({
            type: 'success',
            message: 'Berhasil hapus Perencanaan.',
          })

          queryClient.invalidateQueries(perencanaanQueryKey)

          router.push(PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH)
        },
      }
    )
  }

  return (
    <>
      <main className="max-w-7xl mx-auto py-12 px-6">
        <Header title="Perencanaan Kinerja" className="mb-9" />

        <div className="bg-white rounded-lg">
          <div className="py-9 px-6">
            <section className="space-y-4 mb-10">
              <div className="flex items-center gap-3">
                <Icon className="!text-4xl !w-9 !h-9 !leading-9">
                  account_circle_outlined
                </Icon>
                <Text variant="heading-lg" as="h2">
                  {perencanaanData?.data?.nama}
                </Text>
              </div>
            </section>

            <Detail
              isLoading={isLoading}
              data={resolvedPerencanaanData}
              showReview={true}
            />
          </div>

          <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
            <Button color="blue" size="lg" onClick={handleOpenSepakatiDialog}>
              Sepakati Perencanaan
            </Button>
          </div>
        </div>
      </main>

      <SepakatiDialog
        isOpen={activeAction === 'SEPAKATI'}
        isLoading={sepakatiMutation.isLoading}
        onClose={handleCloseDialog}
        onCancel={handleCancelSepakati}
        onConfirm={handleConfirmSepakati}
      />

      <UlangiDialog
        isOpen={activeAction === 'ULANGI'}
        isLoading={ulangiMutation.isLoading}
        onClose={handleCloseDialog}
        onCancel={handleCloseDialog}
        onConfirm={handleConfirmUlangi}
      />
    </>
  )
}

interface SepakatiDialogProps extends Omit<DialogProps, 'children'> {
  isLoading?: boolean
  onConfirm?: () => void
  onCancel?: () => void
}

const SepakatiDialog = ({
  isLoading,
  onConfirm = () => {},
  onCancel = () => {},
  ...props
}: SepakatiDialogProps) => {
  const [isConfirmed, setIsConfirmed] = useState(false)

  return (
    <Dialog {...props}>
      <DialogOverlay />
      <DialogContent
        aria-labelledby="dialog-sign-skp-title"
        aria-describedby="dialog-sign-skp-description"
      >
        <DialogCloseButton data-testid="btn-close" />
        <div className="mt-12 p-6">
          <img
            alt=""
            src={assetUrl('/illustrations/img-handshake.png')}
            width={84}
            height={84}
            className="flex-shrink-0"
          />
          <Text
            variant="display-sm"
            className="mt-4"
            as="h2"
            id="dialog-sign-skp-title"
          >
            Konfirmasi untuk Sepakati Perencanaan
          </Text>
          <Text
            color="subdued"
            className="mt-4 !text-xl"
            as="p"
            id="dialog-sign-skp-description"
          >
            Perencanaan akan otomatis dikirim ke E-Kinerja dalam 1x24 jam. Jika
            ada yang ingin diubah, Anda dapat ulangi Perencanaan dari awal.
          </Text>

          <div className="mt-4">
            <Checkbox
              checked={isConfirmed}
              disabled={isLoading}
              onChange={(e) => setIsConfirmed(e.target.checked)}
            >
              Saya sepakat dengan Perencanaan yang disetujui oleh Kepala SKB.
            </Checkbox>
          </div>

          <div className="flex gap-5 mt-5">
            <Button
              size="lg"
              color="white"
              disabled={isLoading}
              onClick={onCancel}
            >
              Ulangi dari awal
            </Button>
            <Button
              size="lg"
              disabled={isLoading || !isConfirmed}
              isLoading={isLoading}
              onClick={onConfirm}
            >
              Konfirmasi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const CONFIRMATION_TEXT =
  'Saya menyatakan bahwa saya menyetujui untuk mengulang perencanaan dari awal.'

interface UlangiDialogProps extends Omit<DialogProps, 'children'> {
  isLoading?: boolean
  onConfirm?: () => void
  onCancel?: () => void
}

const UlangiDialog = ({
  isLoading,
  onConfirm = () => {},
  onCancel = () => {},
  ...props
}: UlangiDialogProps) => {
  const [confirmationText, setConfirmationText] = useState('')
  const isConfirmed = confirmationText === CONFIRMATION_TEXT

  // Reset confirmation text when dialog is closed
  useEffect(() => {
    if (!props.isOpen) {
      setConfirmationText('')
    }
  }, [props.isOpen])

  return (
    <Dialog {...props}>
      <DialogOverlay />
      <DialogContent
        aria-labelledby="dialog-ulangi-title"
        aria-describedby="dialog-ulangi-description"
      >
        <DialogCloseButton data-testid="btn-close" />
        <div className="pt-12 p-6">
          <img
            alt=""
            src={assetUrl('/images/errors/img-error-fixing.png')}
            width={84}
            height={84}
            className="flex-shrink-0"
          />
          <Text
            variant="display-sm"
            className="mt-4"
            as="h2"
            id="dialog-ulangi-title"
          >
            Hapus Perencanaan Anda dan ulang dari awal?
          </Text>
          <Text
            color="subdued"
            variant="body-lg"
            className="mt-4"
            as="p"
            id="dialog-ulangi-description"
          >
            Jika lanjut, Perencanaan yang Anda sudah lakukan akan terhapus dan
            Anda harus mengulang Perencanaan dari awal.
          </Text>
          <Text color="subdued" variant="body-lg" className="mt-4">
            Mohon ketik kalimat ini ke kolom kalimat persetujuan:
          </Text>

          <div className="bg-surface-selected-default p-1 my-4">
            <Text color="subdued" variant="body-lg">
              {CONFIRMATION_TEXT}
            </Text>
          </div>

          <Text className="mb-1">Kalimat persetujuan</Text>
          <Textarea
            value={confirmationText}
            placeholder="Ketik di sini."
            onChange={(e) => setConfirmationText(e.target.value)}
          />

          <div className="flex gap-5 mt-5">
            <Button
              size="lg"
              disabled={isLoading || !isConfirmed}
              isLoading={isLoading}
              onClick={onConfirm}
            >
              Lanjut hapus
            </Button>
            <Button
              size="lg"
              color="white"
              disabled={isLoading}
              onClick={onCancel}
            >
              Kembali
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
